import {
  DisconnectOutlined,
  EditOutlined,
  TeamOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { useIntl } from '@umijs/max';
import {
  App,
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import {
  type AdminTeamMember,
  type AdminTeamResponse,
  type CustodyOrder,
  type DepositOrder,
  getAdminCustodyOrders,
  getAdminDeposits,
  getAdminUserTeam,
  getAdminWithdrawals,
  setCustomRate,
  setUserLevel,
  unbindReferral,
  type WithdrawalOrder,
} from '@/services/quant/api';

const { Text } = Typography;

interface UserDetailsModalProps {
  open: boolean;
  userId: string;
  walletAddress: string;
  onClose: () => void;
}

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({
  open,
  userId,
  walletAddress,
  onClose,
}) => {
  const intl = useIntl();
  const { message } = App.useApp();
  const [customRateForm] = Form.useForm();
  const t = (
    id: string,
    defaultMessage: string,
    values?: Record<string, any>,
  ) => intl.formatMessage({ id, defaultMessage }, values);

  const [activeTab, setActiveTab] = useState('deposits');
  const [deposits, setDeposits] = useState<DepositOrder[]>([]);
  const [depositsLoading, setDepositsLoading] = useState(false);
  const [withdrawals, setWithdrawals] = useState<WithdrawalOrder[]>([]);
  const [withdrawalsLoading, setWithdrawalsLoading] = useState(false);
  const [custodyOrders, setCustodyOrders] = useState<CustodyOrder[]>([]);
  const [custodyLoading, setCustodyLoading] = useState(false);
  const [teamData, setTeamData] = useState<AdminTeamResponse | null>(null);
  const [teamLoading, setTeamLoading] = useState(false);

  // Custom rate modal
  const [customRateModalVisible, setCustomRateModalVisible] = useState(false);
  const [savingCustomRate, setSavingCustomRate] = useState(false);
  const [useCustomRate, setUseCustomRate] = useState(false);
  const [useCustomDirectRate, setUseCustomDirectRate] = useState(false);

  // Team tab filters
  const [teamSearch, setTeamSearch] = useState('');
  const [teamLevelFilter, setTeamLevelFilter] = useState<string | undefined>(
    undefined,
  );
  const [teamDepthFilter, setTeamDepthFilter] = useState<number | undefined>(
    undefined,
  );

  // Navigation stack for team drill-down
  const [navStack, setNavStack] = useState<
    { userId: string; walletAddress: string }[]
  >([]);
  const [currentUserId, setCurrentUserId] = useState('');
  const [currentWallet, setCurrentWallet] = useState('');

  // Sync props to current state on open
  useEffect(() => {
    if (open && userId) {
      setCurrentUserId(userId);
      setCurrentWallet(walletAddress);
      setNavStack([]);
    }
  }, [open, userId, walletAddress]);

  const loadDeposits = useCallback(async () => {
    if (!currentWallet) return;
    setDepositsLoading(true);
    try {
      const res = await getAdminDeposits({
        walletAddress: currentWallet,
        pageSize: 100,
      });
      if (res?.success && res?.data) {
        setDeposits(res.data.orders || []);
      }
    } catch {
      // ignore
    } finally {
      setDepositsLoading(false);
    }
  }, [currentWallet]);

  const loadWithdrawals = useCallback(async () => {
    if (!currentUserId) return;
    setWithdrawalsLoading(true);
    try {
      const res = await getAdminWithdrawals({
        userId: currentUserId,
        pageSize: 100,
      });
      if (res?.success && res?.data) {
        setWithdrawals(res.data.orders || []);
      }
    } catch {
      // ignore
    } finally {
      setWithdrawalsLoading(false);
    }
  }, [currentUserId]);

  const loadCustodyOrders = useCallback(async () => {
    if (!currentUserId) return;
    setCustodyLoading(true);
    try {
      const res = await getAdminCustodyOrders({
        userId: currentUserId,
        pageSize: 100,
      });
      if (res?.success && res?.data) {
        setCustodyOrders(res.data.orders || []);
      }
    } catch {
      // ignore
    } finally {
      setCustodyLoading(false);
    }
  }, [currentUserId]);

  const loadTeamData = useCallback(async () => {
    if (!currentUserId) return;
    setTeamLoading(true);
    try {
      const res = await getAdminUserTeam(currentUserId);
      if (res?.success && res?.data) {
        setTeamData(res.data);
      }
    } catch {
      // ignore
    } finally {
      setTeamLoading(false);
    }
  }, [currentUserId]);

  useEffect(() => {
    if (open && currentUserId) {
      loadDeposits();
      loadWithdrawals();
      loadCustodyOrders();
      loadTeamData();
      // Only reset to deposits tab on initial open, not drill-down
      if (navStack.length === 0) {
        setActiveTab('deposits');
      }
      setTeamSearch('');
      setTeamLevelFilter(undefined);
      setTeamDepthFilter(undefined);
    }
  }, [
    open,
    currentUserId,
    loadDeposits,
    loadWithdrawals,
    loadCustodyOrders,
    loadTeamData,
  ]);

  // Team drill-down: navigate to a member
  const navigateToMember = (member: AdminTeamMember) => {
    setNavStack((prev) => [
      ...prev,
      { userId: currentUserId, walletAddress: currentWallet },
    ]);
    setCurrentUserId(member.userId);
    setCurrentWallet(member.walletAddress);
    setActiveTab('team');
  };

  const navigateBack = () => {
    const prev = navStack[navStack.length - 1];
    if (prev) {
      setNavStack((s) => s.slice(0, -1));
      setCurrentUserId(prev.userId);
      setCurrentWallet(prev.walletAddress);
      setActiveTab('team');
    }
  };

  const handleOpenCustomRate = () => {
    const user = teamData?.user;
    const hasCustomRate =
      user?.customRate !== undefined && user?.customRate !== null;
    const hasCustomDirectRate =
      user?.customDirectRate !== undefined && user?.customDirectRate !== null;
    setUseCustomRate(hasCustomRate);
    setUseCustomDirectRate(hasCustomDirectRate);
    customRateForm.setFieldsValue({
      customRate: hasCustomRate ? (user.customRate ?? 0) * 100 : undefined,
      customDirectRate: hasCustomDirectRate
        ? (user.customDirectRate ?? 0) * 100
        : undefined,
      remark: user?.customRateRemark || '',
    });
    setCustomRateModalVisible(true);
  };

  const handleSaveCustomRate = async () => {
    if (!currentUserId) return;
    setSavingCustomRate(true);
    try {
      const values = customRateForm.getFieldsValue();
      const payload: {
        customRate?: number | null;
        customDirectRate?: number | null;
        remark?: string;
      } = {
        remark: values.remark || '',
      };
      payload.customRate = useCustomRate
        ? (values.customRate ?? 0) / 100
        : null;
      payload.customDirectRate = useCustomDirectRate
        ? (values.customDirectRate ?? 0) / 100
        : null;
      const res = await setCustomRate(currentUserId, payload);
      if (res?.success) {
        message.success('Custom rate updated');
        setCustomRateModalVisible(false);
        loadTeamData();
      } else {
        message.error(res?.message || 'Failed to update');
      }
    } catch {
      message.error('Failed to update custom rate');
    } finally {
      setSavingCustomRate(false);
    }
  };

  const handleSetCustomLevel = async (value: string | undefined) => {
    if (!currentUserId) return;
    try {
      const level = value !== undefined ? value : 'auto';
      const res = await setUserLevel(currentUserId, level);
      if (res?.success) {
        message.success(t('pages.userDetails.levelUpdated', 'Level updated'));
        loadTeamData();
      } else {
        message.error(res?.message || 'Failed to update level');
      }
    } catch {
      message.error('Failed to update level');
    }
  };

  const handleUnbindReferral = async () => {
    if (!currentUserId) return;
    try {
      const res = await unbindReferral(currentUserId);
      if (res?.success) {
        message.success(
          t('pages.userDetails.unbindSuccess', 'Referral unbound successfully'),
        );
        loadTeamData();
        loadDeposits();
      } else {
        message.error(res?.message || 'Failed to unbind');
      }
    } catch {
      message.error('Failed to unbind referral');
    }
  };

  const depositStatusColor: Record<string, string> = {
    pending: 'orange',
    confirming: 'blue',
    confirmed: 'green',
    expired: 'default',
    failed: 'red',
  };

  const withdrawalStatusColor: Record<string, string> = {
    pending: 'orange',
    approved: 'green',
    rejected: 'red',
  };

  const custodyStatusColor: Record<string, string> = {
    active: 'blue',
    completed: 'green',
    cancelled: 'red',
  };

  const depositColumns: ColumnsType<DepositOrder> = [
    {
      title: t('pages.userDetails.deposit.orderId', 'Order ID'),
      dataIndex: 'orderId',
      key: 'orderId',
      width: 140,
      ellipsis: true,
    },
    {
      title: t('pages.userDetails.deposit.amount', 'Amount'),
      dataIndex: 'amountFormatted',
      key: 'amount',
      width: 120,
    },
    {
      title: t('pages.userDetails.deposit.status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={depositStatusColor[status] || 'default'}>{status}</Tag>
      ),
    },
    {
      title: t('pages.userDetails.deposit.txHash', 'Tx Hash'),
      dataIndex: 'txHash',
      key: 'txHash',
      width: 140,
      ellipsis: true,
      render: (val: string) => val || '-',
    },
    {
      title: t('pages.userDetails.deposit.createdAt', 'Created'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (text: string) =>
        text ? dayjs(text).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: t('pages.userDetails.deposit.confirmedAt', 'Confirmed'),
      dataIndex: 'confirmedAt',
      key: 'confirmedAt',
      width: 150,
      render: (text: string) =>
        text ? dayjs(text).format('YYYY-MM-DD HH:mm') : '-',
    },
  ];

  const withdrawalColumns: ColumnsType<WithdrawalOrder> = [
    {
      title: t('pages.userDetails.withdrawal.id', 'ID'),
      dataIndex: 'id',
      key: 'id',
      width: 140,
      ellipsis: true,
    },
    {
      title: t('pages.userDetails.withdrawal.amount', 'Amount'),
      dataIndex: 'amountFormatted',
      key: 'amount',
      width: 120,
    },
    {
      title: t('pages.userDetails.withdrawal.status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={withdrawalStatusColor[status] || 'default'}>{status}</Tag>
      ),
    },
    {
      title: t('pages.userDetails.withdrawal.txHash', 'Tx Hash'),
      dataIndex: 'txHash',
      key: 'txHash',
      width: 140,
      ellipsis: true,
      render: (val: string) => val || '-',
    },
    {
      title: t('pages.userDetails.withdrawal.remark', 'Remark'),
      dataIndex: 'remark',
      key: 'remark',
      width: 120,
      ellipsis: true,
      render: (val: string) => val || '-',
    },
    {
      title: t('pages.userDetails.withdrawal.createdAt', 'Created'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (text: string) =>
        text ? dayjs(text).format('YYYY-MM-DD HH:mm') : '-',
    },
  ];

  const custodyColumns: ColumnsType<CustodyOrder> = [
    {
      title: t('pages.userDetails.custody.product', 'Product'),
      key: 'product',
      width: 160,
      render: (_: any, record: CustodyOrder) => (
        <div>
          <div>{record.productNameZh}</div>
          <div style={{ color: '#999', fontSize: 12 }}>
            {record.productNameEn}
          </div>
        </div>
      ),
    },
    {
      title: t('pages.userDetails.custody.amount', 'Amount'),
      dataIndex: 'amount',
      key: 'amount',
      width: 110,
      render: (val: number) => val?.toLocaleString() ?? '-',
    },
    {
      title: t('pages.userDetails.custody.dailyYield', 'Daily Yield'),
      dataIndex: 'dailyYield',
      key: 'dailyYield',
      width: 100,
      render: (val: number) => val?.toFixed(2) ?? '-',
    },
    {
      title: t('pages.userDetails.custody.claimed', 'Claimed'),
      key: 'claimed',
      width: 90,
      render: (_: any, record: CustodyOrder) =>
        `${record.claimedCount}/${record.duration}`,
    },
    {
      title: t('pages.userDetails.custody.totalClaimed', 'Total Claimed'),
      dataIndex: 'totalClaimed',
      key: 'totalClaimed',
      width: 110,
      render: (val: number) => val?.toFixed(2) ?? '-',
    },
    {
      title: t('pages.userDetails.custody.status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (status: string) => (
        <Tag color={custodyStatusColor[status] || 'default'}>{status}</Tag>
      ),
    },
    {
      title: t('pages.userDetails.custody.principal', 'Principal'),
      dataIndex: 'principalWithdrawn',
      key: 'principalWithdrawn',
      width: 90,
      render: (val: boolean) => (
        <Badge
          status={val ? 'success' : 'default'}
          text={
            val
              ? t('pages.userDetails.custody.withdrawn', 'Withdrawn')
              : t('pages.userDetails.custody.locked', 'Locked')
          }
        />
      ),
    },
    {
      title: t('pages.userDetails.custody.period', 'Period'),
      key: 'period',
      width: 180,
      render: (_: any, record: CustodyOrder) => (
        <div style={{ fontSize: 12 }}>
          <div>
            {record.startAt ? dayjs(record.startAt).format('YYYY-MM-DD') : '-'}
          </div>
          <div style={{ color: '#999' }}>
            ~{' '}
            {record.expireAt
              ? dayjs(record.expireAt).format('YYYY-MM-DD')
              : '-'}
          </div>
        </div>
      ),
    },
  ];

  // Team member columns
  const shortenAddress = (addr: string) =>
    addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : '-';

  const teamColumns: ColumnsType<AdminTeamMember> = [
    {
      title: t('pages.userDetails.team.wallet', 'Wallet'),
      dataIndex: 'walletAddress',
      key: 'walletAddress',
      width: 150,
      render: (addr: string, record: AdminTeamMember) =>
        addr ? (
          <Text copyable={{ text: addr }}>
            <a onClick={() => navigateToMember(record)}>
              {shortenAddress(addr)}
            </a>
          </Text>
        ) : (
          '-'
        ),
    },
    {
      title: t('pages.userDetails.team.level', 'Level'),
      dataIndex: 'sLevelName',
      key: 'sLevelName',
      width: 70,
      render: (val: string) => <Tag color="blue">{val}</Tag>,
    },
    {
      title: t('pages.userDetails.team.depth', 'Depth'),
      dataIndex: 'depth',
      key: 'depth',
      width: 70,
      sorter: (a: AdminTeamMember, b: AdminTeamMember) => a.depth - b.depth,
      render: (val: number) =>
        t('pages.userDetails.team.depthValue', 'Lv{val}', { val }),
    },
    {
      title: t('pages.userDetails.team.performance', 'Performance'),
      dataIndex: 'totalPerformance',
      key: 'totalPerformance',
      width: 120,
    },
    {
      title: t('pages.userDetails.team.smallArea', 'Small Area'),
      dataIndex: 'smallAreaPerformance',
      key: 'smallAreaPerformance',
      width: 120,
    },
    {
      title: t('pages.userDetails.team.earnings', 'Earnings'),
      dataIndex: 'totalEarnings',
      key: 'totalEarnings',
      width: 100,
      sorter: (a: AdminTeamMember, b: AdminTeamMember) =>
        a.totalEarnings - b.totalEarnings,
      render: (val: number) => val?.toFixed(2) ?? '-',
    },
    {
      title: t('pages.userDetails.team.directCount', 'Direct'),
      dataIndex: 'directReferralCount',
      key: 'directReferralCount',
      width: 70,
    },
    {
      title: t('pages.userDetails.team.custody', 'Custody'),
      dataIndex: 'hasActiveCustody',
      key: 'hasActiveCustody',
      width: 80,
      render: (val: boolean) => (
        <Badge
          status={val ? 'success' : 'default'}
          text={
            val
              ? t('pages.userDetails.team.yes', 'Yes')
              : t('pages.userDetails.team.no', 'No')
          }
        />
      ),
    },
    {
      title: t('pages.userDetails.team.joinedAt', 'Joined'),
      dataIndex: 'joinedAt',
      key: 'joinedAt',
      width: 120,
      render: (text: string) => (text ? dayjs(text).format('YYYY-MM-DD') : '-'),
    },
  ];

  // Filter team members
  const filteredMembers = (teamData?.members || []).filter((m) => {
    if (
      teamSearch &&
      !m.walletAddress?.toLowerCase().includes(teamSearch.toLowerCase()) &&
      !m.userId?.toLowerCase().includes(teamSearch.toLowerCase())
    ) {
      return false;
    }
    if (teamLevelFilter && m.sLevelName !== teamLevelFilter) return false;
    if (teamDepthFilter !== undefined && m.depth !== teamDepthFilter)
      return false;
    return true;
  });

  // Get unique depths for filter
  const uniqueDepths = [
    ...new Set((teamData?.members || []).map((m) => m.depth)),
  ].sort((a, b) => a - b);

  // Get unique levels for filter
  const uniqueLevels = [
    ...new Set((teamData?.members || []).map((m) => m.sLevelName)),
  ].sort();

  // Team tab content
  const renderTeamTab = () => {
    if (!teamData) return null;
    const { user, earnings, teamSummary } = teamData;

    return (
      <div>
        {navStack.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <a onClick={navigateBack}>
              &larr; {t('pages.userDetails.team.back', 'Back to parent')}
            </a>
          </div>
        )}

        <Row gutter={12} style={{ marginBottom: 16 }}>
          <Col span={4}>
            <Card size="small">
              <Statistic
                title={t('pages.userDetails.team.level', 'Level')}
                value={user.sLevelName}
              />
            </Card>
          </Col>
          <Col span={5}>
            <Card size="small">
              <Statistic
                title={t('pages.userDetails.team.teamSize', 'Team Size')}
                value={teamSummary.totalMembers}
              />
            </Card>
          </Col>
          <Col span={5}>
            <Card size="small">
              <Statistic
                title={t(
                  'pages.userDetails.team.totalPerformance',
                  'Total Performance',
                )}
                value={user.totalPerformance || '0'}
              />
            </Card>
          </Col>
          <Col span={5}>
            <Card size="small">
              <Statistic
                title={t(
                  'pages.userDetails.team.totalEarnings',
                  'Total Earnings',
                )}
                value={earnings.totalEarnings}
                precision={2}
              />
            </Card>
          </Col>
          <Col span={5}>
            <Card size="small">
              <Statistic
                title={t('pages.userDetails.team.smallAreaPerf', 'Small Area')}
                value={user.smallAreaPerformance || '0'}
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={12} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Card size="small">
              <Statistic
                title={t(
                  'pages.userDetails.team.directEarnings',
                  'Direct Earnings',
                )}
                value={earnings.directEarnings}
                precision={2}
                valueStyle={{ fontSize: 16 }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small">
              <Statistic
                title={t(
                  'pages.userDetails.team.levelDiffEarnings',
                  'Level Diff Earnings',
                )}
                value={earnings.levelDiffEarnings}
                precision={2}
                valueStyle={{ fontSize: 16 }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small">
              <div
                style={{
                  fontSize: 12,
                  color: 'rgba(0,0,0,0.45)',
                  marginBottom: 4,
                }}
              >
                {t(
                  'pages.userDetails.team.levelDistribution',
                  'Level Distribution',
                )}
              </div>
              <Space size={4} wrap>
                {Object.entries(teamSummary.levelDistribution || {})
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([level, count]) => (
                    <Tag key={level} color="blue">
                      {level}: {count}
                    </Tag>
                  ))}
              </Space>
            </Card>
          </Col>
        </Row>

        <Row gutter={8} style={{ marginBottom: 12 }}>
          <Col flex="auto">
            <Input
              placeholder={t(
                'pages.userDetails.team.searchPlaceholder',
                'Search by wallet or user ID',
              )}
              value={teamSearch}
              onChange={(e) => setTeamSearch(e.target.value)}
              allowClear
              style={{ maxWidth: 280 }}
            />
          </Col>
          <Col>
            <Select
              placeholder={t(
                'pages.userDetails.team.filterLevel',
                'Filter level',
              )}
              allowClear
              style={{ width: 100 }}
              value={teamLevelFilter}
              onChange={(v) => setTeamLevelFilter(v)}
              options={uniqueLevels.map((l) => ({ label: l, value: l }))}
            />
          </Col>
          <Col>
            <Select
              placeholder={t(
                'pages.userDetails.team.filterDepth',
                'Filter depth',
              )}
              allowClear
              style={{ width: 110 }}
              value={teamDepthFilter}
              onChange={(v) => setTeamDepthFilter(v)}
              options={uniqueDepths.map((d) => ({
                label: t('pages.userDetails.team.depthValue', 'Lv{val}', {
                  val: d,
                }),
                value: d,
              }))}
            />
          </Col>
        </Row>

        <Table
          columns={teamColumns}
          dataSource={filteredMembers}
          rowKey="userId"
          loading={teamLoading}
          size="small"
          pagination={{ pageSize: 10, size: 'small', showSizeChanger: true }}
          scroll={{ x: 950 }}
        />
      </div>
    );
  };

  // Calculate summary stats
  const totalDeposited = deposits
    .filter((d) => d.status === 'confirmed')
    .reduce((sum, d) => sum + Number.parseFloat(d.amountFormatted || '0'), 0);
  const totalWithdrawn = withdrawals
    .filter((w) => w.status === 'approved')
    .reduce((sum, w) => sum + Number.parseFloat(w.amountFormatted || '0'), 0);
  const totalCustodyAmount = custodyOrders
    .filter((c) => c.status === 'active')
    .reduce((sum, c) => sum + c.amount, 0);
  const totalCustodyClaimed = custodyOrders.reduce(
    (sum, c) => sum + c.totalClaimed,
    0,
  );

  return (
    <Modal
      title={
        <span>
          <WalletOutlined style={{ marginRight: 8 }} />
          {t('pages.userDetails.title', 'User Details')}
          {navStack.length > 0 && (
            <Tag color="orange" style={{ marginLeft: 8 }}>
              {t('pages.userDetails.team.drillDown', 'Drill-down')}
            </Tag>
          )}
        </span>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={1060}
      destroyOnClose
    >
      <Descriptions
        size="small"
        column={2}
        style={{ marginBottom: 16 }}
        bordered
      >
        <Descriptions.Item label={t('pages.userDetails.wallet', 'Wallet')}>
          {currentWallet || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('pages.userDetails.userId', 'User ID')}>
          <span style={{ fontSize: 12 }}>{currentUserId}</span>
        </Descriptions.Item>
        <Descriptions.Item
          label={t('pages.userDetails.totalDeposited', 'Total Deposited')}
        >
          <span style={{ color: '#52c41a', fontWeight: 500 }}>
            {totalDeposited.toFixed(2)} USDT
          </span>
        </Descriptions.Item>
        <Descriptions.Item
          label={t('pages.userDetails.totalWithdrawn', 'Total Withdrawn')}
        >
          <span style={{ color: '#ff4d4f', fontWeight: 500 }}>
            {totalWithdrawn.toFixed(2)} USDT
          </span>
        </Descriptions.Item>
        <Descriptions.Item
          label={t('pages.userDetails.activeCustody', 'Active Custody')}
        >
          <span style={{ color: '#1677ff', fontWeight: 500 }}>
            {totalCustodyAmount.toFixed(2)} USDT
          </span>
        </Descriptions.Item>
        <Descriptions.Item
          label={t('pages.userDetails.totalYieldClaimed', 'Yield Claimed')}
        >
          <span style={{ fontWeight: 500 }}>
            {totalCustodyClaimed.toFixed(2)} USDT
          </span>
        </Descriptions.Item>
        <Descriptions.Item
          label={t('pages.userDetails.customLevel', 'Custom Level')}
        >
          <Space>
            <Select
              size="small"
              style={{ width: 120 }}
              value={
                teamData?.user?.customSLevel != null
                  ? `S${teamData.user.customSLevel}`
                  : 'auto'
              }
              onChange={(v) =>
                handleSetCustomLevel(v === 'auto' ? undefined : v)
              }
              options={[
                {
                  label: t('pages.userDetails.autoLevel', 'Auto'),
                  value: 'auto',
                },
                ...Array.from({ length: 8 }, (_, i) => ({
                  label: `S${i}`,
                  value: `S${i}`,
                })),
              ]}
            />
            {teamData?.user?.customSLevel != null && (
              <Tag color="volcano">
                {t('pages.userDetails.locked', 'Locked')}
              </Tag>
            )}
          </Space>
        </Descriptions.Item>
        <Descriptions.Item
          label={
            <Space>
              {t('pages.userDetails.customRate', 'Custom Rate')}
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={handleOpenCustomRate}
                style={{ padding: 0 }}
              />
            </Space>
          }
        >
          {teamData?.user?.customRate != null ? (
            <Tag color="orange">
              {(teamData.user.customRate * 100).toFixed(1)}%
            </Tag>
          ) : (
            <Tag>{t('pages.userDetails.globalRate', 'Global')}</Tag>
          )}{' '}
          {teamData?.user?.customDirectRate != null ? (
            <Tag color="purple">
              Direct: {(teamData.user.customDirectRate * 100).toFixed(1)}%
            </Tag>
          ) : null}
        </Descriptions.Item>
        <Descriptions.Item
          label={t('pages.userDetails.referralBinding', 'Referral')}
        >
          {teamData?.user?.referredBy ? (
            <Space size={4}>
              <Text copyable={{ text: teamData.user.referrerWallet || teamData.user.referredBy }}>
                {teamData.user.referrerWallet
                  ? shortenAddress(teamData.user.referrerWallet)
                  : teamData.user.referredBy.slice(0, 8) + '...'}
              </Text>
              {teamData.user.referredByCode && (
                <Tag>{teamData.user.referredByCode}</Tag>
              )}
              <Popconfirm
                title={t('pages.userDetails.unbindConfirmTitle', 'Unbind Referral')}
                description={t(
                  'pages.userDetails.unbindConfirmDesc',
                  'This will remove the referral relationship and recalculate upline performance. Continue?',
                )}
                onConfirm={handleUnbindReferral}
                okText={t('pages.userDetails.confirm', 'Confirm')}
                cancelText={t('pages.userDetails.cancel', 'Cancel')}
              >
                <Button
                  type="link"
                  danger
                  size="small"
                  icon={<DisconnectOutlined />}
                >
                  {t('pages.userDetails.unbindReferral', 'Unbind')}
                </Button>
              </Popconfirm>
            </Space>
          ) : (
            <Tag>{t('pages.userDetails.notBound', 'Not bound')}</Tag>
          )}
        </Descriptions.Item>
      </Descriptions>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'deposits',
            label: `${t('pages.userDetails.tab.deposits', 'Deposits')} (${deposits.length})`,
            children: (
              <Table
                columns={depositColumns}
                dataSource={deposits}
                rowKey="_id"
                loading={depositsLoading}
                size="small"
                pagination={{ pageSize: 5, size: 'small' }}
                scroll={{ x: 800 }}
              />
            ),
          },
          {
            key: 'withdrawals',
            label: `${t('pages.userDetails.tab.withdrawals', 'Withdrawals')} (${withdrawals.length})`,
            children: (
              <Table
                columns={withdrawalColumns}
                dataSource={withdrawals}
                rowKey="id"
                loading={withdrawalsLoading}
                size="small"
                pagination={{ pageSize: 5, size: 'small' }}
                scroll={{ x: 800 }}
              />
            ),
          },
          {
            key: 'custody',
            label: `${t('pages.userDetails.tab.custody', 'Custody')} (${custodyOrders.length})`,
            children: (
              <Table
                columns={custodyColumns}
                dataSource={custodyOrders}
                rowKey="id"
                loading={custodyLoading}
                size="small"
                pagination={{ pageSize: 5, size: 'small' }}
                scroll={{ x: 900 }}
              />
            ),
          },
          {
            key: 'team',
            label: (
              <span>
                <TeamOutlined style={{ marginRight: 4 }} />
                {t('pages.userDetails.tab.team', 'Team')} (
                {teamData?.teamSummary?.totalMembers ?? 0})
              </span>
            ),
            children: renderTeamTab(),
          },
        ]}
      />

      <Modal
        title={t(
          'pages.userDetails.customRateTitle',
          'Set Custom Commission Rate',
        )}
        open={customRateModalVisible}
        onCancel={() => setCustomRateModalVisible(false)}
        onOk={handleSaveCustomRate}
        confirmLoading={savingCustomRate}
        destroyOnClose
      >
        <Form form={customRateForm} layout="vertical">
          <Form.Item
            label={t('pages.userDetails.levelDiffRate', 'Level-Diff Rate')}
          >
            <Space>
              <Switch
                checked={useCustomRate}
                onChange={setUseCustomRate}
                checkedChildren="Custom"
                unCheckedChildren="Global"
              />
              {useCustomRate && (
                <Form.Item name="customRate" noStyle>
                  <InputNumber
                    min={0}
                    max={100}
                    step={0.1}
                    addonAfter="%"
                    style={{ width: 140 }}
                  />
                </Form.Item>
              )}
            </Space>
          </Form.Item>
          <Form.Item
            label={t('pages.userDetails.directRate', 'Direct Referral Rate')}
          >
            <Space>
              <Switch
                checked={useCustomDirectRate}
                onChange={setUseCustomDirectRate}
                checkedChildren="Custom"
                unCheckedChildren="Global"
              />
              {useCustomDirectRate && (
                <Form.Item name="customDirectRate" noStyle>
                  <InputNumber
                    min={0}
                    max={100}
                    step={0.1}
                    addonAfter="%"
                    style={{ width: 140 }}
                  />
                </Form.Item>
              )}
            </Space>
          </Form.Item>
          <Form.Item
            name="remark"
            label={t('pages.userDetails.remark', 'Remark')}
          >
            <Input.TextArea rows={2} placeholder="Optional remark" />
          </Form.Item>
        </Form>
      </Modal>
    </Modal>
  );
};

export default UserDetailsModal;
